import { SAMPLE_COMPANIES } from '../../components/FrostedGate/sample-companies';
import { KpiSection } from '../../data-models/company-financials.data-model';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import { createFieldDataModel, FieldEntity, IField } from '../../data-models/field2.data-model';
import {
  createKPIRequestDataModel,
  IKPIRequestDataModel,
  KPIRequestFrequency,
  KPIRequestStatus,
} from '../../data-models/kpi-requests.data-model';
import { createKPITemplateDataModel, IKPITemplate } from '../../data-models/kpi-template.data-model';
import {
  createCustomKpiTemplateDefaults,
  CustomKpiTemplateFormValues,
  CustomTemplateSectionType,
  TemplateSectionFormValues,
} from '../../schemas/kpi-template.schema';
import { KpiCollectionCompanySettings } from '../../schemas/KpiCollectionCompanySettings.schema';
import { KpiCollectionConfigurationResponse } from '../../schemas/KpiCollectionConfiguration.schema';
import { createTemplateAssignment, TemplateAssignment } from '../../schemas/template-assignment-schema';
import { getDefaultFormMetaForSection } from './KpiCollection/CustomKpiTemplate/section-utils';

/**
 * Sample rows for the frosted KPI module: one dozen fictional companies with the templates,
 * contacts and requests the Companies, Templates, Requests and Responses screens show.
 */

export const UNIVERSAL_TEMPLATE = 'Foresight Universal';
export const FIRM_TEMPLATES = ['Quarterly Collection', 'Board Pack Metrics'] as const;
export const DEFAULT_COLLECTION_CONFIG = 'All Universal KPIs';

const SECTORS = [
  'Robotics',
  'Healthcare',
  'Software',
  'Media',
  'Energy',
  'Fintech',
  'AI',
  'Biotech',
  'Logistics',
  'Consumer',
  'Security',
  'Data',
];

export interface PreviewKpiCompanyRow {
  companyId: number;
  companyName: string;
  domain: string;
  sector: string;
  reporting: boolean;
  templates: string[];
  configuration: string;
  fye: number;
  respondents: string[];
  ccEmails: string[];
}

function domainOf(name: string): string {
  return `${name.toLowerCase().replace(/[^a-z]/g, '')}.example`;
}

function templatesFor(index: number): string[] {
  if (index % 3 === 2) {
    return [];
  }
  return index % 3 === 0 ? [FIRM_TEMPLATES[0]] : [...FIRM_TEMPLATES];
}

export const PREVIEW_KPI_COMPANIES: PreviewKpiCompanyRow[] = SAMPLE_COMPANIES.map((company, index) => {
  const domain = domainOf(company.name);
  const templates = templatesFor(index);
  return {
    companyId: company.id,
    companyName: company.name,
    domain,
    sector: SECTORS[index % SECTORS.length],
    reporting: index % 4 !== 3,
    templates,
    configuration: templates[0] ?? DEFAULT_COLLECTION_CONFIG,
    fye: company.fye ?? 12,
    respondents: index % 2 === 0 ? [`finance@${domain}`, `ceo@${domain}`] : [`finance@${domain}`],
    ccEmails: index % 3 === 0 ? [`ops@${domain}`] : [],
  };
});

export const PREVIEW_KPI_TEMPLATE = {
  name: FIRM_TEMPLATES[0],
  quantitativeKpis: ['Revenue', 'Gross Margin', 'Net Burn', 'Cash Balance', 'Headcount', 'ARR'],
  availableKpis: ['Customer Count', 'Net Revenue Retention', 'CAC Payback'],
  textQuestion: 'Key highlights this quarter',
  fileUpload: 'Board deck',
  dateField: 'Runway end date',
} as const;

export const PREVIEW_KPI_CONFIGURATION = {
  name: FIRM_TEMPLATES[0],
  kpis: [
    'Revenue',
    'Cost of Goods Sold',
    'Gross Profit',
    'Operating Expenses',
    'EBITDA',
    'Net Income',
    'Cash Balance',
    'Headcount',
  ],
  otherQuestions: ['Key highlights this quarter', 'Hiring plan for next quarter', 'Board deck'],
} as const;

const PERIODS = ['Q2 2026', 'Q1 2026', 'Q4 2025'];

/*
 * Real data models for the live KPI screens the frosted module renders: templates and their
 * assignments, the KPI definitions a template is built from, collection configurations and
 * the legacy request queue. Ids are negative or far above any tenant's.
 */

export const PREVIEW_KPI_TEMPLATES: IKPITemplate[] = FIRM_TEMPLATES.map((name, index) =>
  createKPITemplateDataModel({
    id: 900_101 + index,
    uuid: `00000000-0000-4000-8000-00000090010${index}`,
    name,
  })
);

const templateUuid = (name: string) =>
  PREVIEW_KPI_TEMPLATES.find((template) => template.name === name)?.uuid ?? '';

export const PREVIEW_KPI_TEMPLATE_ASSIGNMENTS: TemplateAssignment[] = PREVIEW_KPI_COMPANIES.flatMap(
  (row, companyIndex) =>
    row.templates.map((template, index) =>
      createTemplateAssignment({
        id: -(companyIndex * 10 + index + 1),
        companyId: row.companyId,
        templateUuid: templateUuid(template),
        frequency:
          template === FIRM_TEMPLATES[1] ? KPIRequestFrequency.Monthly : KPIRequestFrequency.Quarterly,
        respondent: row.respondents,
        secondaryRespondents: row.ccEmails,
        fye: row.fye,
      })
    )
);

const kpiField = (
  id: number,
  displayName: string,
  section: CustomTemplateSectionType,
  entity: FieldEntity = FieldEntity.userKPI
): IField<unknown> =>
  createFieldDataModel({
    id,
    displayName,
    entity,
    entityField: `sample_kpi_${id}`,
    type: section === CustomTemplateSectionType.quantitative ? 'number' : 'string',
    formMeta: getDefaultFormMetaForSection(section),
  });

const UNIVERSAL_KPI_NAMES = PREVIEW_KPI_CONFIGURATION.kpis;
const CUSTOM_KPI_NAMES = [
  ...PREVIEW_KPI_TEMPLATE.quantitativeKpis,
  ...PREVIEW_KPI_TEMPLATE.availableKpis,
].filter((name) => !UNIVERSAL_KPI_NAMES.includes(name as (typeof UNIVERSAL_KPI_NAMES)[number]));

const UNIVERSAL_KPI_FIELDS = UNIVERSAL_KPI_NAMES.map((name, index) =>
  kpiField(900_301 + index, name, CustomTemplateSectionType.quantitative, FieldEntity.universalKPI)
);
const CUSTOM_KPI_FIELDS = CUSTOM_KPI_NAMES.map((name, index) =>
  kpiField(900_401 + index, name, CustomTemplateSectionType.quantitative)
);
const TEXT_KPI_FIELDS = PREVIEW_KPI_CONFIGURATION.otherQuestions
  .filter((name) => name !== PREVIEW_KPI_TEMPLATE.fileUpload)
  .map((name, index) => kpiField(900_501 + index, name, CustomTemplateSectionType.textual));
const FILE_KPI_FIELD = kpiField(900_601, PREVIEW_KPI_TEMPLATE.fileUpload, CustomTemplateSectionType.file);
const DATE_KPI_FIELD = kpiField(900_701, PREVIEW_KPI_TEMPLATE.dateField, CustomTemplateSectionType.date);

/** Every KPI definition the template editor and the configuration screen can offer. */
export const PREVIEW_KPI_FIELDS: IField<unknown>[] = [
  ...UNIVERSAL_KPI_FIELDS,
  ...CUSTOM_KPI_FIELDS,
  ...TEXT_KPI_FIELDS,
  FILE_KPI_FIELD,
  DATE_KPI_FIELD,
];

const fieldId = (name: string) => PREVIEW_KPI_FIELDS.find((field) => field.displayName === name)?.id ?? 0;

const section = (
  type: CustomTemplateSectionType,
  kpis: number[],
  sectionType: KpiSection = KpiSection.actual
): TemplateSectionFormValues => ({ _key: `sample-${type}`, type, sectionType, kpis });

/** The sample template as the editor's form reads it. */
export const PREVIEW_KPI_TEMPLATE_FORM: CustomKpiTemplateFormValues = createCustomKpiTemplateDefaults({
  id: PREVIEW_KPI_TEMPLATES[0].id,
  uuid: PREVIEW_KPI_TEMPLATES[0].uuid,
  name: PREVIEW_KPI_TEMPLATES[0].name,
  sections: [
    section(CustomTemplateSectionType.quantitative, PREVIEW_KPI_TEMPLATE.quantitativeKpis.map(fieldId)),
    section(CustomTemplateSectionType.textual, [fieldId(PREVIEW_KPI_TEMPLATE.textQuestion)]),
    section(CustomTemplateSectionType.file, [fieldId(PREVIEW_KPI_TEMPLATE.fileUpload)]),
    section(CustomTemplateSectionType.date, [fieldId(PREVIEW_KPI_TEMPLATE.dateField)]),
  ],
});

/** What Add New opens: the four empty sections the live editor starts from. */
export const NEW_KPI_TEMPLATE_FORM: CustomKpiTemplateFormValues = createCustomKpiTemplateDefaults({
  sections: [
    section(CustomTemplateSectionType.quantitative, []),
    section(CustomTemplateSectionType.textual, []),
    section(CustomTemplateSectionType.file, []),
    section(CustomTemplateSectionType.date, []),
  ],
});

export const PREVIEW_KPI_CONFIGURATIONS: KpiCollectionConfigurationResponse[] = [
  {
    id: '900201',
    name: FIRM_TEMPLATES[0],
    kpiIds: [...UNIVERSAL_KPI_FIELDS.map((field) => field.id), ...TEXT_KPI_FIELDS.map((field) => field.id)],
  },
  {
    id: '900202',
    name: FIRM_TEMPLATES[1],
    kpiIds: UNIVERSAL_KPI_FIELDS.slice(0, 4).map((field) => field.id),
  },
];

export const PREVIEW_KPI_COMPANY_SETTINGS: KpiCollectionCompanySettings[] = PREVIEW_KPI_COMPANIES.map(
  (row, index) => ({
    id: `900801-${index}`,
    companyId: row.companyId,
    kpiCollectionSettingId: row.templates.length ? PREVIEW_KPI_CONFIGURATIONS[index % 2].id : undefined,
  })
);

const LEGACY_STATUSES: KPIRequestStatus[] = [
  KPIRequestStatus.NotSent,
  KPIRequestStatus.Sent,
  KPIRequestStatus.Submitted,
  KPIRequestStatus.Accepted,
  KPIRequestStatus.NotSent,
  KPIRequestStatus.Sent,
  KPIRequestStatus.Rejected,
  KPIRequestStatus.NotSent,
  KPIRequestStatus.Submitted,
  KPIRequestStatus.Sent,
  KPIRequestStatus.Accepted,
  KPIRequestStatus.NotSent,
];

/** The legacy request queue: one request per company across the queue's statuses. */
export const PREVIEW_LEGACY_REQUESTS: IKPIRequestDataModel[] = PREVIEW_KPI_COMPANIES.map((row, index) => {
  const status = LEGACY_STATUSES[index];
  const template = PREVIEW_KPI_TEMPLATES[index % PREVIEW_KPI_TEMPLATES.length];
  return createKPIRequestDataModel({
    id: -(900_901 + index),
    companyId: row.companyId,
    templateId: template.id,
    templateUuid: template.uuid,
    frequency: KPIRequestFrequency.Quarterly,
    fye: `2026-${String(row.fye).padStart(2, '0')}-28`,
    period: PERIODS[index % PERIODS.length].replace(
      /Q(\d) (\d{4})/,
      (_, quarter, year) => `${year}-${String(Number(quarter) * 3).padStart(2, '0')}-30`
    ),
    respondent: row.respondents,
    secondaryRespondents: row.ccEmails,
    sentAt: status === KPIRequestStatus.NotSent ? null : '2026-07-15T09:00:00.000Z',
    status,
  });
});

/** Sample companies carrying the fields the KPI grids show, for seeding a sample store. */
export const PREVIEW_KPI_COMPANY_MODELS: ICompanyDataModel[] = SAMPLE_COMPANIES.map((company, index) => {
  const row = PREVIEW_KPI_COMPANIES[index];
  return {
    ...company,
    domain: row.domain,
    reporting: row.reporting,
    respondents: row.respondents,
    secondaryRespondents: row.ccEmails,
  };
});
